import { useState, useEffect, useRef } from "react";

interface CountUpMetric {
  raw: string;
  prefix: string;
  value: number;
  suffix: string;
  decimals: number;
}

function parseMetric(metric: string): CountUpMetric | null {
  // Match patterns like "45M+", "$1.2B", "+20 NPS", "4.9★", "60%", "Apple Top AI App 2023"
  const match = metric.match(/^([+$]?)(\d+\.?\d*)\s*([A-Z%★+\w ]*)/i);
  if (!match) return null;

  const prefix = match[1];
  const value = parseFloat(match[2]);
  const suffix = match[3];
  const decimals = match[2].includes(".") ? match[2].split(".")[1].length : 0;

  if (isNaN(value)) return null;
  return { raw: metric, prefix, value, suffix, decimals };
}

function formatValue(current: number, decimals: number): string {
  return decimals > 0 ? current.toFixed(decimals) : Math.floor(current).toString();
}

export function useCountUp(metric: string, inView: boolean, duration = 1800) {
  const [display, setDisplay] = useState(metric);
  const hasAnimated = useRef(false);
  const prefersReducedMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!inView || hasAnimated.current || prefersReducedMotion) return;

    const parsed = parseMetric(metric);
    if (!parsed) return;

    hasAnimated.current = true;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * parsed.value;

      setDisplay(`${parsed.prefix}${formatValue(current, parsed.decimals)}${parsed.suffix}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplay(metric);
      }
    };

    requestAnimationFrame(animate);
  }, [inView, metric, duration, prefersReducedMotion]);

  return display;
}
